import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { TabProvider } from '@tecuity/granite-ui/TabProvider'
import { TabButton } from '@tecuity/granite-ui/TabButton'
import { TabPanel } from '@tecuity/granite-ui/TabPanel'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { Body, Drawer } from 'components/Shell/Shell'
import { Spinner } from 'components/Loaders/Loaders'
import Portal from 'components/Portal/Portal'
import { ConfirmDialog } from 'components/Modal/Modal'
import Revert from '@tecuity/sos-icons/react/revert'
import RightArrowIcon from '@tecuity/sos-icons/react/right-arrow'
import { noop } from 'utilities'
import { setErrorToast, setSuccessToast } from '../../../actions'
import * as api from '../../../requests'
import { HISTORY_FIELDS } from './MiscRecordDetailUtils'
import MiscRecordDetailGeneralTab from './MiscRecordDetailGeneralTab'
import MiscRecordDetailHistoryTab from './MiscRecordDetailHistoryTab'
import MiscRecordDetailCardDrawerContent from './MiscRecordDetailCardDrawerContent'
import MiscRecordDetailFieldDrawerContent from './MiscRecordDetailFieldDrawerContent'
import MiscRecordDetailHistoryDrawerContent from './MiscRecordDetailHistoryDrawerContent'
import MiscRecordDetailHistoryFieldDrawerContent from './MiscRecordDetailHistoryFieldDrawerContent'
import './MiscRecordDetailConfig.scss'

const MiscRecordDetailConfig = ({ selectedModuleId, onDirtyChange }) => {
  const dispatch = useDispatch()
  const [availableFields, setAvailableFields] = useState([])
  const [config, setConfig] = useState(null)
  const [originalConfig, setOriginalConfig] = useState(null)
  const [isDirty, setIsDirty] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [activeTabId, setActiveTabId] = useState('general')
  const [drawer, setDrawer] = useState({ type: null, payload: {} })
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  useEffect(() => {
    if (!selectedModuleId) return
    setIsLoading(true)
    setConfig(null)
    setOriginalConfig(null)
    setIsDirty(false)
    setDrawer({ type: null, payload: {} })
    setDeleteConfirm(null)

    let configResult = null
    let fieldsResult = null
    let configDone = false
    let fieldsDone = false

    const tryFinish = () => {
      if (!configDone || !fieldsDone) return
      const assignedIds = new Set(
        configResult.Cards.flatMap((c) => c.Fields.map((f) => f.MiscellaneousFieldId))
      )
      const unusedFields = fieldsResult.filter((f) => !assignedIds.has(f.MiscellaneousFieldId))
      const initialConfig = { ...configResult, UnusedFields: unusedFields }
      if (!initialConfig.HistoryConfig.Fields || initialConfig.HistoryConfig.Fields.length === 0) {
        initialConfig.HistoryConfig = {
          ...initialConfig.HistoryConfig,
          Fields: HISTORY_FIELDS.map((name, i) => ({ FieldName: name, Label: name, IsEnabled: true, SortOrder: i })),
        }
      }
      setConfig(initialConfig)
      setOriginalConfig(initialConfig)
      setAvailableFields(fieldsResult)
      setIsLoading(false)
    }

    api.getMiscRecordDetailConfig(
      selectedModuleId,
      (res) => {
        configResult = res
        configDone = true
        tryFinish()
      },
      () => {
        dispatch(setErrorToast('Failed to load record detail configuration.'))
        setIsLoading(false)
      }
    )

    api.getMiscRecordDetailAvailableFields(
      selectedModuleId,
      (res) => {
        fieldsResult = res
        fieldsDone = true
        tryFinish()
      },
      () => {
        dispatch(setErrorToast('Failed to load available fields.'))
        setIsLoading(false)
      }
    )
  }, [selectedModuleId])

  const handleAddCard = () => {
    setConfig((prev) => ({
      ...prev,
      Cards: [
        ...prev.Cards,
        {
          CardId: 'new-' + Date.now(),
          Label: '',
          SortOrder: Math.max(...prev.Cards.map((c) => c.SortOrder), -1) + 1,
          Fields: [],
        },
      ],
    }))
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleCardClick = (card) => {
    setDrawer({ type: 'card', payload: card })
  }

  const handleCardLabelChange = (cardId, newLabel) => {
    setConfig((prev) => ({
      ...prev,
      Cards: prev.Cards.map((c) => (c.CardId === cardId ? { ...c, Label: newLabel } : c)),
    }))
    setDrawer((prev) => ({ ...prev, payload: { ...prev.payload, Label: newLabel } }))
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleCardDeleteClick = (card) => {
    setDeleteConfirm({ type: 'card', payload: card })
  }

  const handleCardDeleteConfirm = () => {
    const cardToDelete = deleteConfirm.payload
    setConfig((prev) => ({
      ...prev,
      Cards: prev.Cards.filter((c) => c.CardId !== cardToDelete.CardId),
      UnusedFields: [...prev.UnusedFields, ...cardToDelete.Fields],
    }))
    setDrawer({ type: null, payload: {} })
    setDeleteConfirm(null)
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleFieldClick = (field, cardId) => {
    setDrawer({ type: 'field', payload: { ...field, CardId: cardId } })
  }

  const handleFieldLabelChange = (fieldId, newLabel) => {
    setConfig((prev) => ({
      ...prev,
      Cards: prev.Cards.map((c) => ({
        ...c,
        Fields: c.Fields.map((f) =>
          f.MiscellaneousFieldId === fieldId ? { ...f, Label: newLabel } : f
        ),
      })),
      UnusedFields: prev.UnusedFields.map((f) =>
        f.MiscellaneousFieldId === fieldId ? { ...f, Label: newLabel } : f
      ),
    }))
    setDrawer((prev) => ({ ...prev, payload: { ...prev.payload, Label: newLabel } }))
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleFieldIsEnabledChange = (fieldId, val) => {
    setConfig((prev) => ({
      ...prev,
      Cards: prev.Cards.map((c) => ({
        ...c,
        Fields: c.Fields.map((f) =>
          f.MiscellaneousFieldId === fieldId ? { ...f, IsEnabled: val } : f
        ),
      })),
      UnusedFields: prev.UnusedFields.map((f) =>
        f.MiscellaneousFieldId === fieldId ? { ...f, IsEnabled: val } : f
      ),
    }))
    setDrawer((prev) => ({ ...prev, payload: { ...prev.payload, IsEnabled: val } }))
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleFieldCardChange = (fieldId, newCardId) => {
    const currentCardId = drawer.payload.CardId
    setConfig((prev) => {
      const field =
        prev.Cards.flatMap((c) => c.Fields).find((f) => f.MiscellaneousFieldId === fieldId) ||
        prev.UnusedFields.find((f) => f.MiscellaneousFieldId === fieldId)
      return {
        ...prev,
        UnusedFields:
          currentCardId == null
            ? prev.UnusedFields.filter((f) => f.MiscellaneousFieldId !== fieldId)
            : prev.UnusedFields,
        Cards: prev.Cards.map((c) => {
          if (String(c.CardId) === String(currentCardId) && currentCardId != null) {
            return {
              ...c,
              Fields: c.Fields.filter((f) => f.MiscellaneousFieldId !== fieldId),
            }
          }
          if (String(c.CardId) === String(newCardId)) {
            return {
              ...c,
              Fields: [...c.Fields, { ...field, SortOrder: c.Fields.length }],
            }
          }
          return c
        }),
      }
    })
    setDrawer((prev) => ({ ...prev, payload: { ...prev.payload, CardId: newCardId } }))
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleFieldDeleteClick = (field) => {
    if (field.CardId == null) return
    setDeleteConfirm({ type: 'field', payload: field })
  }

  const handleFieldDeleteConfirm = () => {
    const fieldToDelete = deleteConfirm.payload
    setConfig((prev) => ({
      ...prev,
      Cards: prev.Cards.map((c) => ({
        ...c,
        Fields: c.Fields.filter(
          (f) => f.MiscellaneousFieldId !== fieldToDelete.MiscellaneousFieldId
        ),
      })),
      UnusedFields: [
        ...prev.UnusedFields,
        availableFields.find(
          (f) => f.MiscellaneousFieldId === fieldToDelete.MiscellaneousFieldId
        ) || fieldToDelete,
      ],
    }))
    setDrawer({ type: null, payload: {} })
    setDeleteConfirm(null)
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleMoveFieldUp = (cardId, fieldIndex) => {
    setConfig((prev) => ({
      ...prev,
      Cards: prev.Cards.map((c) => {
        if (c.CardId !== cardId) return c
        const sorted = [...c.Fields].sort((a, b) => a.SortOrder - b.SortOrder)
        const above = sorted[fieldIndex - 1]
        const current = sorted[fieldIndex]
        const aboveOrder = above.SortOrder
        const currentOrder = current.SortOrder
        return {
          ...c,
          Fields: c.Fields.map((f) => {
            if (f.MiscellaneousFieldId === above.MiscellaneousFieldId)
              return { ...f, SortOrder: currentOrder }
            if (f.MiscellaneousFieldId === current.MiscellaneousFieldId)
              return { ...f, SortOrder: aboveOrder }
            return f
          }),
        }
      }),
    }))
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleMoveFieldDown = (cardId, fieldIndex) => {
    setConfig((prev) => ({
      ...prev,
      Cards: prev.Cards.map((c) => {
        if (c.CardId !== cardId) return c
        const sorted = [...c.Fields].sort((a, b) => a.SortOrder - b.SortOrder)
        const current = sorted[fieldIndex]
        const below = sorted[fieldIndex + 1]
        const currentOrder = current.SortOrder
        const belowOrder = below.SortOrder
        return {
          ...c,
          Fields: c.Fields.map((f) => {
            if (f.MiscellaneousFieldId === current.MiscellaneousFieldId)
              return { ...f, SortOrder: belowOrder }
            if (f.MiscellaneousFieldId === below.MiscellaneousFieldId)
              return { ...f, SortOrder: currentOrder }
            return f
          }),
        }
      }),
    }))
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleMoveCardLeft = (cardIndex) => {
    setConfig((prev) => {
      const sorted = [...prev.Cards].sort((a, b) => a.SortOrder - b.SortOrder)
      const left = sorted[cardIndex - 1]
      const current = sorted[cardIndex]
      const leftOrder = left.SortOrder
      const currentOrder = current.SortOrder
      return {
        ...prev,
        Cards: prev.Cards.map((c) => {
          if (c.CardId === left.CardId) return { ...c, SortOrder: currentOrder }
          if (c.CardId === current.CardId) return { ...c, SortOrder: leftOrder }
          return c
        }),
      }
    })
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleMoveCardRight = (cardIndex) => {
    setConfig((prev) => {
      const sorted = [...prev.Cards].sort((a, b) => a.SortOrder - b.SortOrder)
      const current = sorted[cardIndex]
      const right = sorted[cardIndex + 1]
      const currentOrder = current.SortOrder
      const rightOrder = right.SortOrder
      return {
        ...prev,
        Cards: prev.Cards.map((c) => {
          if (c.CardId === current.CardId) return { ...c, SortOrder: rightOrder }
          if (c.CardId === right.CardId) return { ...c, SortOrder: currentOrder }
          return c
        }),
      }
    })
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleHistoryConfigChange = (updatedHistoryConfig) => {
    setConfig((prev) => ({ ...prev, HistoryConfig: updatedHistoryConfig }))
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleHistoryFieldClick = (field) => {
    setDrawer({ type: 'historyField', payload: field })
  }

  const handleHistoryFieldLabelChange = (fieldName, newLabel) => {
    setConfig((prev) => ({
      ...prev,
      HistoryConfig: {
        ...prev.HistoryConfig,
        Fields: prev.HistoryConfig.Fields.map((f) =>
          f.FieldName === fieldName ? { ...f, Label: newLabel } : f
        ),
      },
    }))
    setDrawer((prev) => ({ ...prev, payload: { ...prev.payload, Label: newLabel } }))
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleHistoryFieldIsEnabledChange = (fieldName, val) => {
    setConfig((prev) => ({
      ...prev,
      HistoryConfig: {
        ...prev.HistoryConfig,
        Fields: prev.HistoryConfig.Fields.map((f) =>
          f.FieldName === fieldName ? { ...f, IsEnabled: val } : f
        ),
      },
    }))
    setDrawer((prev) => ({ ...prev, payload: { ...prev.payload, IsEnabled: val } }))
    setIsDirty(true)
    onDirtyChange(true)
  }

  const handleRevert = () => {
    setConfig(originalConfig)
    setIsDirty(false)
    onDirtyChange(false)
    setDrawer({ type: null, payload: {} })
    setDeleteConfirm(null)
  }

  const handleSaveConfirm = () => {
    setDeleteConfirm(null)
    setIsSaving(true)
    api.saveMiscRecordDetailConfig(
      { ...config, MiscellaneousModuleId: selectedModuleId },
      (res) => {
        const assignedIds = new Set(
          res.Cards.flatMap((c) => c.Fields.map((f) => f.MiscellaneousFieldId))
        )
        const unusedFields = availableFields.filter(
          (f) => !assignedIds.has(f.MiscellaneousFieldId)
        )
        const savedHistoryConfig = res.HistoryConfig || {}
        const historyFields =
          savedHistoryConfig.Fields && savedHistoryConfig.Fields.length > 0
            ? savedHistoryConfig.Fields
            : HISTORY_FIELDS.map((name, i) => ({ FieldName: name, Label: name, IsEnabled: true, SortOrder: i }))
        const newConfig = {
          ...res,
          UnusedFields: unusedFields,
          HistoryConfig: { ...savedHistoryConfig, Fields: historyFields },
        }
        setConfig(newConfig)
        setOriginalConfig(newConfig)
        setDrawer({ type: null, payload: {} })
        setIsDirty(false)
        setIsSaving(false)
        onDirtyChange(false)
        dispatch(setSuccessToast('Record Detail Config Saved'))
      },
      () => {
        setIsSaving(false)
        dispatch(setErrorToast('Failed to Save Record Detail Config'))
      }
    )
  }

  const handleSave = () => {
    if (config.Cards.some((c) => c.Fields.length === 0)) {
      setDeleteConfirm({ type: 'emptyCards', payload: {} })
      return
    }
    handleSaveConfirm()
  }

  const drawerTitle =
    drawer.type === 'card'
      ? 'Edit Card'
      : drawer.type === 'field'
      ? 'Edit Field'
      : drawer.type === 'history'
      ? 'Filing History'
      : drawer.type === 'historyField'
      ? 'Edit History Field'
      : ''

  const drawerSubtitle =
    drawer.type === 'card'
      ? drawer.payload.Label || ''
      : drawer.type === 'field'
      ? drawer.payload.DbFieldName || ''
      : drawer.type === 'historyField'
      ? drawer.payload.FieldName || ''
      : ''

  return (
    <Body showDrawer={!isLoading && drawer.type !== null}>
      <div className="misc-record-detail-config">
        {isLoading && <Spinner size="medium" themeColor="dark-neutral" />}
        {!isLoading && config && (
          <>
            <TabProvider activeTabId={activeTabId} onChange={(id) => { setActiveTabId(id); setDrawer({ type: null, payload: {} }) }}>
              <div className="tablist" role="tablist">
                <TabButton id="general" forPanelId="general-panel" label="General" />
                <TabButton id="history" forPanelId="history-panel" label="History" />
              </div>
              <TabPanel tabId="general" id="general-panel">
                <MiscRecordDetailGeneralTab
                  cards={config.Cards}
                  unusedFields={config.UnusedFields}
                  selectedCardId={drawer.type === 'card' ? drawer.payload.CardId : null}
                  selectedFieldId={
                    drawer.type === 'field' ? drawer.payload.MiscellaneousFieldId : null
                  }
                  onCardClick={handleCardClick}
                  onFieldClick={handleFieldClick}
                  onAddCard={handleAddCard}
                  onMoveFieldUp={handleMoveFieldUp}
                  onMoveFieldDown={handleMoveFieldDown}
                  onMoveCardLeft={handleMoveCardLeft}
                  onMoveCardRight={handleMoveCardRight}
                />
              </TabPanel>
              <TabPanel tabId="history" id="history-panel">
                <MiscRecordDetailHistoryTab
                  historyConfig={config.HistoryConfig}
                  isSelected={drawer.type === 'history'}
                  onHistoryBoxClick={() => setDrawer({ type: 'history', payload: {} })}
                  selectedHistoryFieldName={
                    drawer.type === 'historyField' ? drawer.payload.FieldName : null
                  }
                  onHistoryFieldClick={handleHistoryFieldClick}
                />
              </TabPanel>
            </TabProvider>
            <div className={`context-bar-wrapper${drawer.type !== null ? ' drawer-opened' : ''}`}>
              <ContextBar>
                <div className="context-button-wrapper">
                  <ContextButton
                    variant="danger"
                    onClick={handleRevert}
                    disabled={!isDirty}
                    leftIcon={
                      <div className="checkmark-wrapper left">
                        <Revert />
                      </div>
                    }
                  >
                    Revert
                  </ContextButton>
                  <ContextButton
                    variant="primary"
                    onClick={handleSave}
                    disabled={!isDirty}
                    rightIcon={
                      <div className="checkmark-wrapper right">
                        <RightArrowIcon />
                      </div>
                    }
                  >
                    Save
                  </ContextButton>
                </div>
              </ContextBar>
            </div>
          </>
        )}
      </div>
      <Drawer
        showCloseButton
        disableFocusTrap
        show={!isLoading && drawer.type !== null}
        title={drawerTitle}
        subtitle={drawerSubtitle}
        onSidebarDismiss={() => setDrawer({ type: null, payload: {} })}
      >
        {drawer.type === 'card' && (
          <MiscRecordDetailCardDrawerContent
            card={drawer.payload}
            onLabelChange={(newLabel) => handleCardLabelChange(drawer.payload.CardId, newLabel)}
            onDeleteClick={() => handleCardDeleteClick(drawer.payload)}
          />
        )}
        {drawer.type === 'field' && config && (
          <MiscRecordDetailFieldDrawerContent
            field={drawer.payload}
            cards={config.Cards}
            onLabelChange={(newLabel) =>
              handleFieldLabelChange(drawer.payload.MiscellaneousFieldId, newLabel)
            }
            onIsEnabledChange={(val) =>
              handleFieldIsEnabledChange(drawer.payload.MiscellaneousFieldId, val)
            }
            onCardChange={(newCardId) =>
              handleFieldCardChange(drawer.payload.MiscellaneousFieldId, newCardId)
            }
            onDeleteClick={() => handleFieldDeleteClick(drawer.payload)}
          />
        )}
        {drawer.type === 'history' && config && (
          <MiscRecordDetailHistoryDrawerContent
            historyConfig={config.HistoryConfig}
            onChange={handleHistoryConfigChange}
          />
        )}
        {drawer.type === 'historyField' && config && (
          <MiscRecordDetailHistoryFieldDrawerContent
            field={drawer.payload}
            onLabelChange={(v) => handleHistoryFieldLabelChange(drawer.payload.FieldName, v)}
            onIsEnabledChange={(v) => handleHistoryFieldIsEnabledChange(drawer.payload.FieldName, v)}
          />
        )}
      </Drawer>

      <Portal isOpened={deleteConfirm?.type === 'card'}>
        <ConfirmDialog
          closeModal={() => setDeleteConfirm(null)}
          onModalClosed={noop}
          message="Are you sure you want to delete this card? All fields will be moved to the Unused Fields card."
          buttons={[
            { label: 'No', onClick: () => setDeleteConfirm(null), className: 'btn-default' },
            { label: 'Yes', onClick: handleCardDeleteConfirm, className: 'btn-danger' },
          ]}
        />
      </Portal>

      <Portal isOpened={deleteConfirm?.type === 'field'}>
        <ConfirmDialog
          closeModal={() => setDeleteConfirm(null)}
          onModalClosed={noop}
          message="Are you sure you want to delete this field? It will be moved to the Unused Fields card."
          buttons={[
            { label: 'No', onClick: () => setDeleteConfirm(null), className: 'btn-default' },
            { label: 'Yes', onClick: handleFieldDeleteConfirm, className: 'btn-danger' },
          ]}
        />
      </Portal>

      <Portal isOpened={deleteConfirm?.type === 'emptyCards'}>
        <ConfirmDialog
          closeModal={() => setDeleteConfirm(null)}
          onModalClosed={noop}
          message="One or more cards have no fields and will be removed when saved. Do you want to continue?"
          buttons={[
            { label: 'Cancel', onClick: () => setDeleteConfirm(null), className: 'btn-default' },
            { label: 'Confirm', onClick: handleSaveConfirm, className: 'btn-primary' },
          ]}
        />
      </Portal>
    </Body>
  )
}

export default MiscRecordDetailConfig
